import { useEffect, useRef, useState } from "react";
import { CATEGORY_DEFINITIONS, PRIMARY_CATEGORY_TILES, findCategoryDefinition } from "@shared/rules-engine/categories";
import { api, ApiClientError } from "../../lib/api";
import Icon, { type IconName } from "../../components/Icon";
import type { ContentAnswer, ContentCategory, ContentClarification, RulesEvaluationResult, SelectedContent } from "@shared/types";

interface ClarifyingQuestion {
  question: string;
  options: string[] | null;
}

interface Props {
  shipmentId: string;
  initial: SelectedContent[];
  onSubmit: (contents: SelectedContent[]) => void;
  submitting: boolean;
}

// Step 2 + 3 — "What are you sending?". The tile picker (TileContentsStep)
// is opt-in via an admin toggle (functions/lib/settings.ts) — by default
// senders only see the free-text describe-and-check flow
// (DescribeContentsStep). Both ultimately call the same PATCH .../contents
// endpoint, which runs the real rules engine — the AI classifier never
// decides postability itself, only which categories/answers to submit.
export default function ContentsStep(props: Props) {
  const [tilePickerEnabled, setTilePickerEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    api
      .getSettings()
      .then((s) => setTilePickerEnabled(s.tilePickerEnabled))
      .catch(() => setTilePickerEnabled(false));
  }, []);

  if (tilePickerEnabled === null) {
    return (
      <div className="page" style={{ display: "flex", justifyContent: "center", padding: "var(--space-6)" }}>
        <div className="spinner" role="status" aria-label="Loading" />
      </div>
    );
  }

  return tilePickerEnabled ? <TileContentsStep {...props} /> : <DescribeContentsStep {...props} />;
}

// ----------------------------------------------------------------------
// Default flow: describe it, AI classifies, the real rules engine gives an
// immediate preliminary verdict (parcel size/carrier not known yet at this
// point, so a later hard-block found at the rates step is still possible —
// worded as "nothing obviously blocked" rather than a final guarantee).
// ----------------------------------------------------------------------

type DescribePhase = "describe" | "processing" | "clarify" | "questions" | "verdict";

interface DescribeContentsProps extends Props {
  initialDescription?: string;
  autoStart?: boolean;
  onBack?: () => void;
}

function unansweredVisibleQuestions(nextContents: SelectedContent[]): number {
  return nextContents.reduce((count, content) => {
    const definition = findCategoryDefinition(content.category);
    if (!definition) return count;
    return (
      count +
      definition.questions.filter((question) => {
        if (question.showIf && content.answers[question.showIf.key] !== question.showIf.equals) return false;
        return content.answers[question.key] === undefined;
      }).length
    );
  }, 0);
}

function DescribeContentsStep({
  shipmentId,
  onSubmit,
  submitting,
  initialDescription = "",
  autoStart = false,
  onBack,
}: DescribeContentsProps) {
  const [phase, setPhase] = useState<DescribePhase>(autoStart ? "processing" : "describe");
  const [description, setDescription] = useState(initialDescription);
  const [error, setError] = useState<string | null>(null);
  const [understood, setUnderstood] = useState<ContentCategory[]>([]);
  const [contents, setContents] = useState<SelectedContent[]>([]);
  const [evaluation, setEvaluation] = useState<RulesEvaluationResult | null>(null);
  const [clarifyingQuestion, setClarifyingQuestion] = useState<ClarifyingQuestion | null>(null);
  const [clarifyAnswer, setClarifyAnswer] = useState("");
  const [clarifications, setClarifications] = useState<ContentClarification[]>([]);
  const [usedLocalFallback, setUsedLocalFallback] = useState(false);
  const autoStarted = useRef(false);

  async function runClassification(nextClarifications: ContentClarification[]) {
    setPhase("processing");
    setError(null);
    try {
      const result = await api.classifyContents(shipmentId, description.trim(), nextClarifications);
      setUsedLocalFallback(result.usedLocalFallback);

      const repeatedQuestion = result.clarifyingQuestion
        ? nextClarifications.some(
            (entry) => entry.question.trim().toLowerCase() === result.clarifyingQuestion!.question.trim().toLowerCase(),
          )
        : false;
      if (result.clarifyingQuestion && !repeatedQuestion) {
        setClarifyingQuestion(result.clarifyingQuestion);
        setClarifyAnswer("");
        setPhase("clarify");
        return;
      }

      if (result.categories.length === 0) {
        setError("Couldn't work out what that is — try describing it a bit differently (e.g. \"a paperback book\" or \"a bottle of perfume\").");
        setPhase("describe");
        return;
      }
      const nextContents: SelectedContent[] = result.categories.map((category) => ({
        category,
        answers: result.answers[category] ?? {},
      }));
      setUnderstood(result.categories);
      setContents(nextContents);

      // The model is asked to surface material ambiguity itself. This form is
      // the safety net: if it omits an answer that the rules catalogue knows
      // is relevant, the sender still cannot silently skip it.
      if (unansweredVisibleQuestions(nextContents) > 0) {
        setPhase("questions");
        return;
      }

      const updated = await api.updateContents(shipmentId, nextContents);
      setEvaluation(updated.rulesEvaluation);
      setPhase("verdict");
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Couldn't check that right now — please try again.");
      setPhase("describe");
    }
  }

  useEffect(() => {
    if (!autoStart || autoStarted.current) return;
    autoStarted.current = true;
    void runClassification([]);
    // This is intentionally a one-shot hand-off from the tile page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function check() {
    if (!description.trim()) return;
    setClarifications([]);
    void runClassification([]);
  }

  function submitClarification(answer: string) {
    if (!answer.trim() || !clarifyingQuestion) return;
    const nextClarifications = [
      ...clarifications,
      { question: clarifyingQuestion.question, answer: answer.trim() },
    ];
    setClarifications(nextClarifications);
    void runClassification(nextClarifications);
  }

  function setContentAnswer(category: ContentCategory, key: string, value: boolean | string | number) {
    setContents((current) =>
      current.map((content) =>
        content.category === category
          ? { ...content, answers: { ...content.answers, [key]: value } }
          : content,
      ),
    );
  }

  async function submitQuestions() {
    if (unansweredVisibleQuestions(contents) > 0) return;
    setPhase("processing");
    setError(null);
    try {
      const updated = await api.updateContents(shipmentId, contents);
      setEvaluation(updated.rulesEvaluation);
      setPhase("verdict");
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Couldn't check that right now — please try again.");
      setPhase("questions");
    }
  }

  function returnToDescription() {
    if (onBack) {
      onBack();
      return;
    }
    setPhase("describe");
    setEvaluation(null);
  }

  if (phase === "processing") {
    return (
      <div className="stack fade-in-up" style={{ alignItems: "center", textAlign: "center", paddingTop: "var(--space-6)" }}>
        <div className="spinner" role="status" aria-label="Analysing what you're sending" />
        <div>
          <h1>Analysing what you're sending</h1>
          <p style={{ color: "var(--color-text-muted)" }}>Checking the item details and whether we need to ask anything else.</p>
        </div>
      </div>
    );
  }

  if (phase === "clarify" && clarifyingQuestion) {
    return (
      <div className="stack fade-in-up">
        <div>
          <h1>Quick question</h1>
          <p style={{ color: "var(--color-text-muted)" }}>{clarifyingQuestion.question}</p>
        </div>

        {clarifyingQuestion.options ? (
          <div className="tile-grid stagger-in" role="group" aria-label={clarifyingQuestion.question}>
            {clarifyingQuestion.options.map((opt) => (
              <button key={opt} type="button" className="tile" onClick={() => submitClarification(opt)}>
                <span className="tile-label">{opt}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="card stack-sm">
            <input
              aria-label={clarifyingQuestion.question}
              value={clarifyAnswer}
              onChange={(e) => setClarifyAnswer(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitClarification(clarifyAnswer)}
              placeholder="Your answer"
            />
            <button className="btn btn-primary" onClick={() => submitClarification(clarifyAnswer)} disabled={!clarifyAnswer.trim()}>
              Continue
            </button>
          </div>
        )}

        <button className="btn btn-ghost" onClick={returnToDescription}>
          Back
        </button>
      </div>
    );
  }

  if (phase === "questions") {
    return (
      <div className="stack fade-in-up">
        <div>
          <h1>A few quick questions</h1>
          <p style={{ color: "var(--color-text-muted)" }}>
            Answer anything we couldn't safely work out from your description.
          </p>
          {usedLocalFallback && (
            <p className="field-help">The full AI assistant is temporarily unavailable, so we're checking the important details directly.</p>
          )}
        </div>

        {contents.map((content) => {
          const definition = findCategoryDefinition(content.category);
          if (!definition || definition.questions.length === 0) return null;
          return (
            <div className="card stack-sm" key={content.category}>
              <strong>{definition.label}</strong>
              {definition.questions.map((question) => {
                if (question.showIf && content.answers[question.showIf.key] !== question.showIf.equals) return null;
                return (
                  <div className="field" key={question.key}>
                    <label htmlFor={`${content.category}-${question.key}`}>{question.label}</label>
                    {question.helpText && <span className="field-help">{question.helpText}</span>}
                    {question.type === "boolean" ? (
                      <div className="tag-row" role="radiogroup" aria-label={question.label}>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ width: "auto", flex: 1 }}
                          aria-pressed={content.answers[question.key] === true}
                          onClick={() => setContentAnswer(content.category, question.key, true)}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ width: "auto", flex: 1 }}
                          aria-pressed={content.answers[question.key] === false}
                          onClick={() => setContentAnswer(content.category, question.key, false)}
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <select
                        id={`${content.category}-${question.key}`}
                        value={(content.answers[question.key] as string) ?? ""}
                        onChange={(event) => setContentAnswer(content.category, question.key, event.target.value)}
                      >
                        <option value="" disabled>
                          Choose an option
                        </option>
                        {question.options?.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}

        {error && <span className="field-error">{error}</span>}
        <div className="stack-sm">
          <button
            className="btn btn-primary"
            onClick={() => void submitQuestions()}
            disabled={submitting || unansweredVisibleQuestions(contents) > 0}
          >
            Continue
          </button>
          <button className="btn btn-ghost" onClick={returnToDescription}>
            Back
          </button>
        </div>
      </div>
    );
  }

  if (phase === "verdict" && evaluation) {
    const blocked = evaluation.overallOutcome === "NO_COMPATIBLE_CARRIER";
    const needsManualCheck = evaluation.overallOutcome === "MANUAL_CHECK_REQUIRED";
    const ok = !blocked && !needsManualCheck;

    return (
      <div className="stack fade-in-up">
        <div>
          <h1>Here's what we found</h1>
          <p style={{ color: "var(--color-text-muted)" }}>
            We understood this as: {understood.map((c) => findCategoryDefinition(c)?.shortLabel ?? c).join(", ")}
          </p>
          {usedLocalFallback && (
            <p className="field-help">Checked using basic matching — the full AI assistant is temporarily unavailable.</p>
          )}
        </div>

        {ok && (
          <div className="banner banner-success">
            <span>✓ Nothing here is blocked — you're good to continue. (Final carrier options depend on size/weight, checked next.)</span>
          </div>
        )}

        {needsManualCheck && (
          <div className="banner banner-warn">
            <span>⚠ This needs a manual check before we can confirm it's postable. Please contact Michael directly before sending.</span>
          </div>
        )}

        {blocked && (
          <div className="banner banner-danger" role="alert">
            <div className="stack-sm">
              {evaluation.guidance.map((g, i) => (
                <p key={i} style={{ margin: 0 }}>
                  {g}
                </p>
              ))}
            </div>
          </div>
        )}

        <div className="stack-sm">
          {!blocked && (
            <button className="btn btn-primary" onClick={() => onSubmit(contents)} disabled={submitting}>
              {submitting ? "Continuing…" : "Continue"}
            </button>
          )}
          <button
            className="btn btn-ghost"
            onClick={returnToDescription}
          >
            {blocked ? "Try describing it differently" : "Start again"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="stack fade-in-up">
      <div>
        <h1>What are you sending?</h1>
        <p style={{ color: "var(--color-text-muted)" }}>Type a product name or describe everything you're sending.</p>
      </div>

      <div className="card stack-sm">
        <textarea
          id="description"
          aria-label="What are you sending?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) check();
          }}
          placeholder="e.g. a box of chocolates and a hand-knitted jumper"
          rows={3}
          style={{
            width: "100%",
            padding: "var(--space-3)",
            borderRadius: "var(--radius-sm)",
            border: "1px solid var(--color-border)",
            background: "var(--color-surface)",
            color: "var(--color-text)",
            resize: "vertical",
          }}
        />
        {error && <span className="field-error">{error}</span>}
      </div>

      <button className="btn btn-primary" onClick={check} disabled={!description.trim()}>
        Continue
      </button>
      {onBack && (
        <button className="btn btn-ghost" onClick={onBack}>
          Back
        </button>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// Tile picker — kept for admins to re-enable (functions/lib/settings.ts)
// as a manual fallback, e.g. if the AI helper is unavailable or a sender
// prefers not to type a description.
// ----------------------------------------------------------------------

function TileContentsStep({ shipmentId, initial, onSubmit, submitting }: Props) {
  const [selected, setSelected] = useState<Set<ContentCategory>>(new Set(initial.map((c) => c.category)));
  const [showMore, setShowMore] = useState(initial.some((c) => !PRIMARY_CATEGORY_TILES.includes(c.category)));
  const [answers, setAnswers] = useState<Record<string, ContentAnswer>>(
    Object.fromEntries(initial.map((c) => [c.category, c.answers])),
  );
  const [phase, setPhase] = useState<"select" | "questions">("select");
  const [description, setDescription] = useState("");
  const [describing, setDescribing] = useState(false);

  function toggle(category: ContentCategory) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }

  function setAnswer(category: ContentCategory, key: string, value: boolean | string | number) {
    setAnswers((prev) => ({ ...prev, [category]: { ...prev[category], [key]: value } }));
  }

  const selectedList = [...selected];
  const categoriesWithQuestions = selectedList
    .map((c) => findCategoryDefinition(c))
    .filter((d) => d && d.questions.length > 0);

  function submit() {
    const contents: SelectedContent[] = selectedList.map((category) => ({
      category,
      answers: answers[category] ?? {},
    }));
    onSubmit(contents);
  }

  if (describing) {
    return (
      <DescribeContentsStep
        shipmentId={shipmentId}
        initial={initial}
        onSubmit={onSubmit}
        submitting={submitting}
        initialDescription={description.trim()}
        autoStart
        onBack={() => setDescribing(false)}
      />
    );
  }

  if (phase === "select") {
    const tiles = showMore ? CATEGORY_DEFINITIONS : CATEGORY_DEFINITIONS.filter((c) => PRIMARY_CATEGORY_TILES.includes(c.category));
    return (
      <div className="stack fade-in-up">
        <div>
          <h1>What are you sending?</h1>
          <p style={{ color: "var(--color-text-muted)" }}>Select everything that applies — you can pick more than one.</p>
        </div>

        <div className="card stack-sm">
          <label htmlFor="description" style={{ fontWeight: 600, fontSize: "0.9rem" }}>
            Or just describe it
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. a box of chocolates and a hand-knitted jumper"
            rows={2}
            style={{ width: "100%", padding: "var(--space-3)", borderRadius: "var(--radius-sm)", border: "1px solid var(--color-border)", background: "var(--color-surface)", color: "var(--color-text)", resize: "vertical" }}
          />
          <button type="button" className="btn btn-secondary" onClick={() => setDescribing(true)} disabled={!description.trim()}>
            Continue
          </button>
        </div>

        <div className="tile-grid stagger-in" role="group" aria-label="What are you sending?">
          {tiles.map((c) => (
            <button
              key={c.category}
              type="button"
              className="tile"
              aria-pressed={selected.has(c.category)}
              onClick={() => toggle(c.category)}
            >
              <span className="tile-icon">
                <Icon name={c.icon as IconName} />
              </span>
              <span className="tile-label">{c.shortLabel}</span>
            </button>
          ))}
        </div>

        {!showMore && (
          <button type="button" className="btn btn-ghost" onClick={() => setShowMore(true)}>
            Show more categories
          </button>
        )}

        <button
          className="btn btn-primary"
          disabled={selected.size === 0}
          onClick={() => (categoriesWithQuestions.length > 0 ? setPhase("questions") : submit())}
        >
          Continue
        </button>
      </div>
    );
  }

  return (
    <div className="stack fade-in-up">
      <div>
        <h1>A few quick questions</h1>
        <p style={{ color: "var(--color-text-muted)" }}>This helps us find services that can actually accept your parcel.</p>
      </div>

      {categoriesWithQuestions.map((def) => (
        <div className="card stack-sm" key={def!.category}>
          <strong>{def!.label}</strong>
          {def!.questions.map((q) => {
            if (q.showIf) {
              const dependentValue = answers[def!.category]?.[q.showIf.key];
              if (dependentValue !== q.showIf.equals) return null;
            }
            return (
              <div className="field" key={q.key}>
                <label htmlFor={`${def!.category}-${q.key}`}>{q.label}</label>
                {q.helpText && <span className="field-help">{q.helpText}</span>}
                {q.type === "boolean" ? (
                  <div className="tag-row" role="radiogroup" aria-label={q.label}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ width: "auto", flex: 1 }}
                      aria-pressed={answers[def!.category]?.[q.key] === true}
                      onClick={() => setAnswer(def!.category, q.key, true)}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ width: "auto", flex: 1 }}
                      aria-pressed={answers[def!.category]?.[q.key] === false}
                      onClick={() => setAnswer(def!.category, q.key, false)}
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <select
                    id={`${def!.category}-${q.key}`}
                    value={(answers[def!.category]?.[q.key] as string) ?? ""}
                    onChange={(e) => setAnswer(def!.category, q.key, e.target.value)}
                  >
                    <option value="" disabled>
                      Choose an option
                    </option>
                    {q.options?.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <div className="stack-sm">
        <button className="btn btn-primary" onClick={submit} disabled={submitting}>
          {submitting ? "Checking…" : "Continue"}
        </button>
        <button className="btn btn-ghost" onClick={() => setPhase("select")}>
          Back
        </button>
      </div>
    </div>
  );
}
